import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

const LocationContext = createContext();

export const LocationProvider = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const [location, setLocation] = useState(null);
  const [locationStatus, setLocationStatus] = useState('idle'); // idle, requesting, granted, denied, error
  const [locationError, setLocationError] = useState('');
  const [locationAddress, setLocationAddress] = useState('');
  const [showPermissionModal, setShowPermissionModal] = useState(false);

  // Request location when user logs in (especially for owners)
  useEffect(() => {
    if (isAuthenticated && user) {
      // Check if we already have permission
      if (navigator.permissions) {
        navigator.permissions.query({ name: 'geolocation' }).then((result) => {
          if (result.state === 'granted') {
            requestLocation();
          } else if (result.state === 'prompt') {
            // Show modal to ask user to allow location
            if (user.role === 'owner') {
              setShowPermissionModal(true);
            }
          } else {
            setLocationStatus('denied');
          }
        }).catch(() => {
          // Fallback for browsers that don't support permissions API
          if (user.role === 'owner') {
            setShowPermissionModal(true);
          }
        });
      } else {
        // Fallback for browsers that don't support permissions API
        if (user.role === 'owner') {
          setShowPermissionModal(true);
        }
      }
    }
  }, [isAuthenticated, user]);

  const requestLocation = async () => {
    if (!navigator.geolocation) {
      setLocationStatus('error');
      setLocationError('Geolocation is not supported by your browser');
      return false;
    }

    setLocationStatus('requesting');
    setLocationError('');
    setShowPermissionModal(false);

    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          setLocation({ latitude, longitude });
          setLocationStatus('granted');

          // Try to get address from coordinates (reverse geocoding)
          try {
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
            );
            const data = await response.json();
            if (data.display_name) {
              setLocationAddress(data.display_name);
            }
          } catch (err) {
            console.log('Could not fetch address:', err);
          }

          resolve(true);
        },
        (error) => {
          setLocationStatus('denied');
          switch (error.code) {
            case error.PERMISSION_DENIED:
              setLocationError('Location permission denied. Please allow location access in your browser settings.');
              break;
            case error.POSITION_UNAVAILABLE:
              setLocationError('Location information is unavailable.');
              break;
            case error.TIMEOUT:
              setLocationError('Location request timed out. Please try again.');
              break;
            default:
              setLocationError('An unknown error occurred while getting location.');
          }
          resolve(false);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      );
    });
  };

  const dismissModal = () => {
    setShowPermissionModal(false);
  };

  return (
    <LocationContext.Provider value={{
      location,
      locationStatus,
      locationError,
      locationAddress,
      requestLocation,
      showPermissionModal,
      dismissModal
    }}>
      {children}

      {/* Location Permission Modal */}
      {showPermissionModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2">
          <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full overflow-hidden animate-fade-in">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-4 py-5 text-white">
              <div className="flex items-center justify-center mb-3">
                <div className="bg-white/20 p-3 rounded-full">
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
              </div>
              <h2 className="text-xl font-bold text-center">Enable Location</h2>
              <p className="text-blue-100 text-center text-sm mt-1">For better service and verification</p>
            </div>

            {/* Content */}
            <div className="px-4 py-4">
              <p className="text-gray-600 text-center text-sm mb-4">
                We need your location to verify your business address.
              </p>

              <div className="space-y-2 mb-4">
                <div className="flex items-center text-sm text-gray-700">
                  <svg className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Verify your business location
                </div>
                <div className="flex items-center text-sm text-gray-700">
                  <svg className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Required for document verification
                </div>
                <div className="flex items-center text-sm text-gray-700">
                  <svg className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Secure and private
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={dismissModal}
                  className="flex-1 px-3 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium text-sm"
                >
                  Later
                </button>
                <button
                  onClick={requestLocation}
                  className="flex-1 px-3 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm flex items-center justify-center"
                >
                  <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  </svg>
                  Allow Location
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </LocationContext.Provider>
  );
};

export const useLocation = () => {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error('useLocation must be used within a LocationProvider');
  }
  return context;
};

export default LocationContext;
